import React, { useState, useEffect, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { useApp } from '../../context/AppContext';
import VoiceVisualizer from './VoiceVisualizer';
import QueryVisualizer from './QueryVisualizer';
import FirstTimeToast from './FirstTimeToast';
import CollapsibleStatus from './CollapsibleStatus';
import { globalAudioManager } from '../../services/globalAudioManager';
import { deviceManager } from '../../services/deviceManager';
import { audioPlaybackService } from '../../services/audioPlaybackService';
import { Settings, Mic, Square, MessageSquare } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// ⚠️ UNIVERSAL DEVICE COMPATIBILITY: Cross-platform utilities
const DeviceUtils = {
  // Detect platform type
  isMobile: () => {
    if (typeof window === 'undefined') return false;
    
    // Check for React Native environment
    if (typeof navigator !== 'undefined' && navigator.product === 'ReactNative') {
      return true;
    }
    
    // Check for mobile user agents (Android, iOS)
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    return /android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
  },
  
  // Detect if running in Electron
  isElectron: () => {
    return typeof window !== 'undefined' && 
           window.process && 
           window.process.type === 'renderer';
  },
  
  // Get platform-specific storage
  getStorage: () => {
    if (typeof window === 'undefined') return null;
    
    // React Native AsyncStorage simulation for web
    if (typeof localStorage !== 'undefined') {
      return {
        getItem: (key) => {
          try {
            return localStorage.getItem(key);
          } catch (e) {
            return null;
          }
        },
        setItem: (key, value) => {
          try {
            localStorage.setItem(key, value);
            return Promise.resolve();
          } catch (e) {
            return Promise.reject(e);
          }
        }
      };
    }
    
    return null;
  },
  
  // Platform-specific timing for state sync
  getStateSyncDelay: () => {
    const isMobile = DeviceUtils.isMobile();
    return isMobile ? 150 : 50; // Slightly longer delay for mobile
  },
  
  // Check if device supports WebAudio
  supportsWebAudio: () => {
    return typeof window !== 'undefined' && 
           (window.AudioContext || window.webkitAudioContext);
  },
  
  // Get platform-specific config for universal compatibility
  getPlatformConfig: () => {
    const isMobile = DeviceUtils.isMobile();
    const isElectron = DeviceUtils.isElectron();
    
    if (isMobile) {
      return {
        particleCount: 600,
        renderScale: 0.7,
        updateFrequency: 30,
        buttonDelay: 200,
        useTouch: true,
        lowPower: true
      };
    } else if (isElectron) {
      return {
        particleCount: 2000,
        renderScale: 1.0,
        updateFrequency: 60,
        buttonDelay: 100,
        useTouch: false,
        lowPower: false
      };
    } else {
      return {
        particleCount: 1800,
        renderScale: 1.0,
        updateFrequency: 60,
        buttonDelay: 150,
        useTouch: false,
        lowPower: false
      };
    }
  }
};

const DashboardContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing.xl};
  background: ${({ theme }) => theme.colors.dark.bg};
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const WelcomeSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const WelcomeText = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.dark.text};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const SubText = styled.p`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
`;

const QuickActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
`;

const ActionButton = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.dark.elevated};
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  font-size: 0.875rem;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  position: relative;
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary[500]};
    color: ${({ theme }) => theme.colors.primary[400]};
    background: ${({ theme }) => theme.colors.primary[500]}10;
  }
  
  &.highlighted {
    border-color: ${({ theme }) => theme.colors.primary[500]};
    background: ${({ theme }) => theme.colors.primary[500]}20;
    box-shadow: 0 0 20px ${({ theme }) => theme.colors.primary[500]}40;
    animation: glow 2s ease-in-out infinite alternate;
  }
  
  @keyframes glow {
    from {
      box-shadow: 0 0 20px ${({ theme }) => theme.colors.primary[500]}40;
    }
    to {
      box-shadow: 0 0 30px ${({ theme }) => theme.colors.primary[500]}60;
    }
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

// Add the new circular mode toggle button
const ModeToggleButton = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${({ theme, $isTextMode }) => 
    $isTextMode 
      ? `linear-gradient(135deg, ${theme.colors.primary[500]}, ${theme.colors.accent.purple})`
      : `linear-gradient(135deg, ${theme.colors.accent.orange}, ${theme.colors.accent.red})`
  };
  border: 2px solid ${({ theme }) => theme.colors.dark.border};
  color: white;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  box-shadow: ${({ theme }) => theme.shadows.md};
  position: relative;
  z-index: 100;
  
  &:hover {
    transform: scale(1.1);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

// Add tooltip for the mode toggle button
const ModeToggleTooltip = styled.div`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: ${({ theme }) => theme.colors.dark.elevated};
  color: ${({ theme }) => theme.colors.dark.text};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: 0.75rem;
  white-space: nowrap;
  box-shadow: ${({ theme }) => theme.shadows.md};
  z-index: 1000;
  margin-top: ${({ theme }) => theme.spacing.sm};
  opacity: 0;
  transition: opacity 0.2s ease;
  
  &.visible {
    opacity: 1;
  }
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  gap: ${({ theme }) => theme.spacing.xl};
  min-height: 0;
  position: relative;
`;

const CenterPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.dark.surface};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
  position: relative;
  overflow: hidden;
`;

const BackgroundGradient = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at center, 
    ${({ theme }) => theme.colors.primary[500]}05 0%, 
    transparent 70%
  );
  pointer-events: none;
`;

const TextQueryInput = styled(motion.div)`
  position: absolute;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  background: ${({ theme }) => theme.colors.dark.elevated};
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  width: 80%;
  max-width: 500px;
  z-index: 100;
  box-shadow: ${({ theme }) => theme.shadows.lg};
`;

const TextQueryForm = styled.form`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const TextQueryInputField = styled.input`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ theme }) => theme.colors.dark.surface};
  color: ${({ theme }) => theme.colors.dark.text};
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary[500]};
  }
`;

const TextQueryButton = styled.button`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.primary[500]};
  color: white;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  cursor: pointer;
  font-weight: 500;
  
  &:hover {
    background: ${({ theme }) => theme.colors.primary[600]};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CloseTextQueryButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  cursor: pointer;
  font-size: 1.2rem;
  
  &:hover {
    color: ${({ theme }) => theme.colors.dark.text};
  }
`;

// Add Text Mode UI Components
const TextModeContainer = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing.xl};
  z-index: 50;
  
  // Add subtle background gradient
  background: radial-gradient(
    circle at center,
    ${({ theme }) => theme.colors.primary[500]}05 0%,
    transparent 70%
  );
`;

const TextModeHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  border-bottom: 1px solid ${({ theme }) => theme.colors.dark.border};
`;

const TextModeTitle = styled.h2`
  color: ${({ theme }) => theme.colors.dark.text};
  font-size: 1.4rem;
  font-weight: 600;
  background: linear-gradient(135deg, 
    ${({ theme }) => theme.colors.primary[400]}, 
    ${({ theme }) => theme.colors.accent.purple}
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const TextModeContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.dark.elevated};
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  overflow: hidden;
  gap: ${({ theme }) => theme.spacing.md};
  box-shadow: ${({ theme }) => theme.shadows.md};
`;

const TextResponseArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.colors.dark.surface};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.dark.text};
  font-size: 1rem;
  line-height: 1.6;
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
  
  // Remove gradient, keep solid background
  background: ${({ theme }) => theme.colors.dark.surface};
  
  // Add inner shadow for depth
  box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.15);
  
  // Show scrollbar only when needed
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.dark.bg};
    border-radius: 4px;
    visibility: hidden;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.dark.border};
    border-radius: 4px;
    visibility: hidden;
  }
  
  // Show scrollbar only when hovering or scrolling
  &:hover::-webkit-scrollbar-track,
  &:hover::-webkit-scrollbar-thumb {
    visibility: visible;
  }
  
  // Add default message styling
  &:empty::before {
    content: "Your responses will appear here...";
    color: ${({ theme }) => theme.colors.dark.textSecondary};
    font-style: italic;
    display: block;
    text-align: center;
    margin-top: 20%;
  }
  
  // Markdown styling
  p {
    margin: 0 0 1em 0;
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  strong {
    font-weight: 600;
    color: ${({ theme }) => theme.colors.primary.main};
  }
  
  em {
    font-style: italic;
    color: ${({ theme }) => theme.colors.dark.textSecondary};
  }
  
  code {
    background: ${({ theme }) => theme.colors.dark.bg};
    padding: 2px 6px;
    border-radius: 4px;
    font-family: 'Courier New', monospace;
    font-size: 0.9em;
  }
  
  ul, ol {
    margin: 0.5em 0;
    padding-left: 1.5em;
  }
  
  li {
    margin: 0.25em 0;
  }
  
  h1, h2, h3, h4, h5, h6 {
    margin: 1em 0 0.5em 0;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.primary.main};
  }
  
  // Table styling for markdown tables
  table {
    width: 100%;
    border-collapse: collapse;
    margin: 1em 0;
    background: ${({ theme }) => theme.colors.dark.bg};
    border-radius: ${({ theme }) => theme.borderRadius.sm};
    overflow: hidden;
  }
  
  thead {
    background: ${({ theme }) => theme.colors.dark.border};
  }
  
  th {
    padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
    text-align: left;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.primary.main};
    border-bottom: 2px solid ${({ theme }) => theme.colors.primary[500]};
  }
  
  td {
    padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
    border-bottom: 1px solid ${({ theme }) => theme.colors.dark.border};
    color: ${({ theme }) => theme.colors.dark.text};
  }
  
  tr:last-child td {
    border-bottom: none;
  }
  
  tr:hover {
    background: ${({ theme }) => theme.colors.dark.surface};
  }
  
  // Alternating row colors for better readability
  tbody tr:nth-child(even) {
    background: rgba(255, 255, 255, 0.02);
  }
`;

const TextInputContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const TextQueryArea = styled.textarea`
  width: 100%;
  height: 100px;
  padding: ${({ theme }) => theme.spacing.md};
  padding-right: 120px; /* Space for the send button */
  background: ${({ theme }) => theme.colors.dark.surface};
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.dark.text};
  font-size: 1rem;
  font-family: inherit;
  resize: none;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary[500]};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary[500]}40;
  }
  
  // Add placeholder styling
  &::placeholder {
    color: ${({ theme }) => theme.colors.dark.textSecondary};
    font-style: italic;
  }
  
  // Add subtle inner shadow for depth
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const TextSendButtonContainer = styled.div`
  position: absolute;
  bottom: 28px; /* Position above the character counter */
  right: ${({ theme }) => theme.spacing.md};
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const TextSubmitButton = styled(motion.button)`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.colors.primary[500]};
  color: white;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: 500;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 80px;
  box-shadow: ${({ theme }) => theme.shadows.md};
  height: 36px;
  
  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.primary[600]};
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
  
  &:active:not(:disabled) {
    transform: translateY(1px);
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.8;
  }
`;

const SendButtonText = styled.span`
  font-size: 0.9rem;
  margin-bottom: 1px;
  font-weight: 600;
  letter-spacing: 0.3px;
`;

const ShortcutText = styled.span`
  font-size: 0.55rem;
  opacity: 0.85;
  font-weight: 400;
  letter-spacing: 0.5px;
`;

const TextQueryCounter = styled.div`
  align-self: flex-end;
  font-size: 0.7rem;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  margin-top: -20px; /* Position it closer to the text area */
  margin-right: 6px;
`;

// Add loading dots animation for processing state
const LoadingDots = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  
  span {
    width: 6px;
    height: 6px;
    background: white;
    border-radius: 50%;
    display: inline-block;
    margin: 0 2px;
    animation: pulse 1.5s infinite;
    opacity: 0.7;
    
    &:nth-child(1) {
      animation-delay: 0s;
    }
    
    &:nth-child(2) {
      animation-delay: 0.5s;
    }
    
    &:nth-child(3) {
      animation-delay: 1s;
    }
  }
  
  @keyframes pulse {
    0%, 100% {
      transform: scale(1);
      opacity: 0.7;
    }
    50% {
      transform: scale(1.2);
      opacity: 1;
    }
  }
`;

const QueryButton = styled(motion.button)`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  background: ${({ theme }) => theme.colors.primary[500]};
  border: 1px solid ${({ theme }) => theme.colors.primary[500]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: white;
  font-size: 1rem;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  z-index: 100;
  box-shadow: ${({ theme }) => theme.shadows.md};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  font-weight: 500;
  
  &:hover {
    background: ${({ theme }) => theme.colors.primary[600]};
    border-color: ${({ theme }) => theme.colors.primary[600]};
  }
`;

const TextQueryHint = styled.div`
  position: absolute;
  bottom: 20px;
  right: 20px;
  font-size: 0.7rem;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  pointer-events: none;
`;

const Dashboard = ({ onNavigate }) => {
  const { user } = useAuth();
  const { isListening, voiceData, toggleVoiceListening, dashboardState, updateDashboardState } = useApp();
  
  // Use context state instead of local state for persistence
  const {
    isQueryMode,
    queryStatus,
    transcribedText,
    llmResponse,
    queryAudioStream,
    isTextMode,
    textQuery,
    textResponse,
    isTextProcessing,
    isBackgroundListening,
    showButton
  } = dashboardState;
  
  // Local states that don't need persistence across navigation
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [showFirstTimeToast, setShowFirstTimeToast] = useState(false);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);

  // Add refs for media recording
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  
  // Add refs for text mode auto-scroll
  const textResponseRef = useRef(null);
  const textUserHasScrolledUp = useRef(false);

  // UNIVERSAL DEVICE COMPATIBILITY: Cross-platform state management
  const storage = DeviceUtils.getStorage();
  const platformConfig = useMemo(() => DeviceUtils.getPlatformConfig(), []);
  const isMobileDevice = DeviceUtils.isMobile();
  
  // Track if this is the initial mount to prevent unnecessary state changes
  const isInitialMount = useRef(true);
  const buttonTimerRef = useRef(null);
  
  // UNIVERSAL DEVICE COMPATIBILITY: Platform-adaptive initialization
  useEffect(() => {
    // Only run setup once on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      
      const savedPreference = storage?.getItem('backgroundListening');
      const initialValue = savedPreference !== null ? JSON.parse(savedPreference) : true;
      
      // Only update if different from current state to prevent unnecessary re-renders
      if (initialValue !== isBackgroundListening) {
        updateDashboardState({ isBackgroundListening: initialValue });
      }
      
      // Initialize audio system if supported and enabled
      if (initialValue && DeviceUtils.supportsWebAudio()) {
        globalAudioManager.initialize().then(() => {
          if (!globalAudioManager.isPersistentMode) {
            globalAudioManager.startPersistentProcessing();
          }
        });
      }
      
      buttonTimerRef.current = setTimeout(() => {
        updateDashboardState({ showButton: !initialValue }); // Show button if background listening is disabled
      }, platformConfig.buttonDelay);
    }
    
    return () => {
      if (buttonTimerRef.current) {
        clearTimeout(buttonTimerRef.current);
      }
    };
  }, []); // Empty dependency array - maintains persistent state across navigation

  // Auto-scroll functionality for text mode streaming response
  useEffect(() => {
    const textArea = textResponseRef.current;
    if (!textArea) return;

    const isNearBottom = () => {
      const { scrollTop, scrollHeight, clientHeight } = textArea;
      return scrollHeight - scrollTop - clientHeight < 100;
    };

    // Handle scroll event - detect if user manually scrolled up
    const handleScroll = () => {
      if (isNearBottom()) {
        textUserHasScrolledUp.current = false; // User scrolled back to bottom
      } else {
        textUserHasScrolledUp.current = true; // User scrolled up
      }
    };

    // Auto-scroll to bottom if user hasn't scrolled up
    const scrollToBottom = () => {
      if (!textUserHasScrolledUp.current && isTextProcessing) {
        textArea.scrollTop = textArea.scrollHeight;
      }
    };

    textArea.addEventListener('scroll', handleScroll);
    scrollToBottom(); // Scroll when content changes

    return () => {
      textArea.removeEventListener('scroll', handleScroll);
    };
  }, [textResponse, isTextProcessing]);

  // Reset scroll state when starting new text query
  useEffect(() => {
    if (isTextProcessing && textResponse) {
      textUserHasScrolledUp.current = false; // Reset for new response
    }
  }, [isTextProcessing]);

  useEffect(() => {
    // Update button visibility when background listening state changes
    if (buttonTimerRef.current) {
      clearTimeout(buttonTimerRef.current);
    }
    
    buttonTimerRef.current = setTimeout(() => {
      updateDashboardState({ showButton: !isBackgroundListening });
    }, platformConfig.buttonDelay);
    
    return () => {
      if (buttonTimerRef.current) {
        clearTimeout(buttonTimerRef.current);
      }
    };
  }, [isBackgroundListening, platformConfig.buttonDelay]);

  useEffect(() => {
    // Simulate connection status changes
    const timer = setTimeout(() => {
      setConnectionStatus('connected');
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Show toast only once per login session when background listening is enabled
    const userId = user?.uid || user?.id;
    
    if (userId && connectionStatus === 'connected' && isBackgroundListening) {
      // Check if toast was already shown for this login session
      const sessionKey = `toast_shown_session_${userId}`;
      const currentSessionId = `session_${Date.now()}`; // Use timestamp as session identifier
      const lastShownSession = sessionStorage.getItem(sessionKey);
      
      // Check if this is a new login session (not just page refresh)
      const isNewLoginSession = !sessionStorage.getItem(`login_session_${userId}`);
      
      if (isNewLoginSession || !lastShownSession) {
        // Mark this as current login session
        sessionStorage.setItem(`login_session_${userId}`, currentSessionId);
        
        // Show toast for new login session
        const toastTimer = setTimeout(() => {
          setShowFirstTimeToast(true);
          // Mark toast as shown for this session
          sessionStorage.setItem(sessionKey, currentSessionId);
        }, 500);
        
        return () => clearTimeout(toastTimer);
      }
    }
  }, [user, connectionStatus, isBackgroundListening]);



  useEffect(() => {
    const handleSettingsChange = async (event) => {
      if (event.detail.backgroundListening !== undefined) {
        const newValue = event.detail.backgroundListening;
        updateDashboardState({ isBackgroundListening: newValue });
        
        // Update storage in a platform-agnostic way
        if (storage) {
          try {
            await storage.setItem('backgroundListening', JSON.stringify(newValue));
          } catch (error) {
            console.warn('Failed to save background listening preference:', error);
          }
        }
        
        // Update audio manager state if audio is supported
        if (DeviceUtils.supportsWebAudio()) {
          try {
            if (newValue) {
              await globalAudioManager.initialize();
              if (!globalAudioManager.isPersistentMode) {
                await globalAudioManager.startPersistentProcessing();
              }
            } else {
              globalAudioManager.stopPersistentProcessing();
            }
          } catch (audioError) {
            console.warn('Audio manager update failed:', audioError);
          }
        }
      }
    };
    
    window.addEventListener('settingsChanged', handleSettingsChange);
    
    return () => {
      window.removeEventListener('settingsChanged', handleSettingsChange);
    };
  }, [storage]);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const handleEnableListening = async () => {
    try {
      if (DeviceUtils.supportsWebAudio()) {
        await toggleVoiceListening();
      } else {
        console.warn('WebAudio not supported on this platform, enabling visual-only mode');
      }
      
      updateDashboardState({ isBackgroundListening: true });
      
      // Update storage in platform-agnostic way
      if (storage) {
        try {
          await storage.setItem('backgroundListening', 'true');
        } catch (error) {
          console.warn('Failed to save background listening preference:', error);
        }
      }
      
      // Trigger settings change event
      window.dispatchEvent(new CustomEvent('settingsChanged', {
        detail: { backgroundListening: true }
      }));
    } catch (error) {
      console.error('❌ Failed to enable microphone:', error);
      updateDashboardState({ isBackgroundListening: false });
      
      // Update storage with error state
      if (storage) {
        try {
          await storage.setItem('backgroundListening', 'false');
        } catch (storageError) {
          console.warn('Failed to save error state:', storageError);
        }
      }
      
      window.dispatchEvent(new CustomEvent('settingsChanged', {
        detail: { backgroundListening: false }
      }));
    }
  };

  // Function to stop query mode
  const stopQueryMode = (processAudio = true) => {
    updateDashboardState({ queryStatus: 'processing' });
    
    if (autoStopTimerRef.current) {
      clearTimeout(autoStopTimerRef.current);
      autoStopTimerRef.current = null;
      console.log('✅ Auto-stop timer cleared');
    }
    
    // Stop the audio stream
    if (queryAudioStream) {
      queryAudioStream.getTracks().forEach(track => track.stop());
      updateDashboardState({ queryAudioStream: null });
    }
    
    if (mediaRecorderRef.current) {
      if (mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
        console.log('✅ MediaRecorder stopped');
      }
      // Remove event listeners to prevent memory leaks
      mediaRecorderRef.current.ondataavailable = null;
      mediaRecorderRef.current = null;
    }
    
    if (vadAudioContextRef.current && vadAudioContextRef.current.state !== 'closed') {
      vadAudioContextRef.current.close();
      vadAudioContextRef.current = null;
      console.log('✅ VAD AudioContext closed');
    }
    
    // Clear any existing VAD timeout
    if (vadTimeoutRef.current) {
      clearTimeout(vadTimeoutRef.current);
      vadTimeoutRef.current = null;
    }
    
    // Process the recorded audio only if requested
    if (processAudio) {
      setTimeout(() => {
        if (audioChunksRef.current.length > 0) {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm;codecs=opus' });
          console.log('Sending recorded audio to backend for processing');
          sendQueryAudioToBackend(audioBlob);
          
          audioChunksRef.current = [];
          console.log('✅ Audio chunks cleared after creating blob');
        } else {
          // No audio recorded, simulate response
          updateDashboardState({ 
            queryStatus: 'response',
            transcribedText: 'No audio detected. Please try speaking louder or closer to the microphone.'
          });
          
          // Reset after showing response for a while
          setTimeout(async () => {
            updateDashboardState({ 
              isQueryMode: false,
              queryStatus: 'idle',
              transcribedText: ''
            });
            audioChunksRef.current = [];
            
            // Ensure background listening is restarted when returning to listening mode
            if (isBackgroundListening && DeviceUtils.supportsWebAudio()) {
              try {
                // Initialize and restart global audio manager
                await globalAudioManager.initialize();
                if (!globalAudioManager.isPersistentMode) {
                  await globalAudioManager.startPersistentProcessing();
                }
                
                // Dispatch event to notify VoiceVisualizer to restart audio processing
                window.dispatchEvent(new CustomEvent('queryModeEnded'));
              } catch (error) {
                console.warn('Failed to restart background listening after query mode:', error);
              }
            }
          }, 3000);
        }
      }, 500);
    } else {
      // Just reset without processing audio
      updateDashboardState({ 
        isQueryMode: false,
        queryStatus: 'idle',
        transcribedText: ''
      });
      audioChunksRef.current = [];
      
      // Ensure background listening is restarted when returning to listening mode
      if (isBackgroundListening && DeviceUtils.supportsWebAudio()) {
        // Use a setTimeout to handle the async operations
        setTimeout(async () => {
          try {
            // Initialize and restart global audio manager
            await globalAudioManager.initialize();
            if (!globalAudioManager.isPersistentMode) {
              await globalAudioManager.startPersistentProcessing();
            }
            
            // Dispatch event to notify VoiceVisualizer to restart audio processing
            window.dispatchEvent(new CustomEvent('queryModeEnded'));
          } catch (error) {
            console.warn('Failed to restart background listening after query mode:', error);
          }
        }, 0);
      }
    }
  };

  // Function to send query audio to backend
  const sendQueryAudioToBackend = async (audioBlob) => {
    try {
      const token = localStorage.getItem('sarvik_token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      // Step 1: Send audio for transcription
      const transcribeFormData = new FormData();
      transcribeFormData.append('audio', audioBlob, 'query_recording.webm');
      transcribeFormData.append('token', token);
      
      console.log('Sending audio to /api/query/process endpoint for transcription');
      
      // Use the correct backend URL (port 8000) instead of frontend URL (port 3000)
      const backendURL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const transcribeResponse = await fetch(`${backendURL}/api/query/process`, {
        method: 'POST',
        body: transcribeFormData,
      });
      
      if (!transcribeResponse.ok) {
        throw new Error(`HTTP error! status: ${transcribeResponse.status}`);
      }
      
      const transcribeResult = await transcribeResponse.json();
      console.log('Transcription result:', transcribeResult);
      
      // Show the transcribed text immediately
      if (transcribeResult.success && transcribeResult.query) {
        updateDashboardState({ 
          transcribedText: transcribeResult.query,
          queryStatus: 'transcribed'
        });
        
        // Step 2: Send transcribed text for LLM processing with streaming
        const llmRequestBody = {
          token: token,
          query: transcribeResult.query
        };
        
        console.log('Sending transcribed text to /api/query/voice-stream-with-audio endpoint');
        
        // Initialize TTS audio playback
        try {
          await audioPlaybackService.initialize();
          await audioPlaybackService.connectToTTS('ws://localhost:8002');
          console.log('✅ Audio playback service connected');
        } catch (audioError) {
          console.warn('⚠️ TTS audio not available:', audioError);
          // Continue with text-only mode if TTS fails
        }
        
        // Use fetch with streaming response (NEW endpoint with audio support)
        const response = await fetch(`${backendURL}/api/query/voice-stream-with-audio`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(llmRequestBody),
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        // Process the streaming response
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let accumulatedResponse = '';
        
        try {
          while (true) {
            const { done, value } = await reader.read();
            
            if (done) {
              // Streaming is complete
              updateDashboardState({ queryStatus: 'response' });
              break;
            }
            
            // Decode the chunk and process SSE format
            const chunk = decoder.decode(value, { stream: true });
            const lines = chunk.split('\n\n');
            
            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const data = JSON.parse(line.slice(6)); // Remove 'data: ' prefix
                  
                  if (data.error) {
                    console.error('LLM streaming error:', data.error);
                    break;
                  }
                  
                  if (data.done) {
                    // Streaming is complete
                    updateDashboardState({ queryStatus: 'response' });
                    // Mark audio stream as ended
                    audioPlaybackService.markStreamEnded();
                    break;
                  }
                  
                  if (data.token) {
                    // Add new token to accumulated response
                    accumulatedResponse += data.token;
                    updateDashboardState({ llmResponse: accumulatedResponse });
                    
                    // Switch to response state when we start receiving tokens
                    if (queryStatus !== 'response') {
                      updateDashboardState({ queryStatus: 'response' });
                    }
                  }
                } catch (parseError) {
                  console.warn('Error parsing SSE data:', parseError);
                }
              }
            }
          }
        } finally {
          reader.releaseLock();
        }
      }
      
      // Dynamic timeout: minimum 5 seconds OR when audio completes, whichever is later
      let minTimeoutPassed = false;
      let audioComplete = false;
      
      const resetQueryMode = async () => {
        updateDashboardState({ 
          isQueryMode: false,
          queryStatus: 'idle',
          transcribedText: '',
          llmResponse: ''
        });
        audioChunksRef.current = []; // Clear audio chunks
        
        // Cleanup audio playback service
        audioPlaybackService.cleanup();
        
        // Ensure background listening is restarted when returning to listening mode
        if (isBackgroundListening && DeviceUtils.supportsWebAudio()) {
          try {
            // Initialize and restart global audio manager
            await globalAudioManager.initialize();
            if (!globalAudioManager.isPersistentMode) {
              await globalAudioManager.startPersistentProcessing();
            }
            
            // Dispatch event to notify VoiceVisualizer to restart audio processing
            window.dispatchEvent(new CustomEvent('queryModeEnded'));
          } catch (error) {
            console.warn('Failed to restart background listening after query mode:', error);
          }
        }
      };
      
      const checkAndReset = () => {
        if (minTimeoutPassed && audioComplete) {
          console.log('✅ Both minimum timeout and audio playback completed - resetting query mode');
          resetQueryMode();
        }
      };
      
      // Minimum 5 second timeout
      setTimeout(() => {
        console.log('⏱️ Minimum 5 second timeout passed');
        minTimeoutPassed = true;
        checkAndReset();
      }, 5000);
      
      // Set callback for when audio playback completes
      audioPlaybackService.onPlaybackComplete = () => {
        console.log('🔊 Audio playback completed');
        audioComplete = true;
        checkAndReset();
      };
      
    } catch (error) {
      console.error('Failed to send query audio to backend:', error);
      // Still show response UI even if there's an error
      updateDashboardState({ 
        queryStatus: 'response',
        transcribedText: 'Error processing your query. Please try again.'
      });
      
      // Reset after showing response for a while
      setTimeout(async () => {
        updateDashboardState({ 
          isQueryMode: false,
          queryStatus: 'idle',
          transcribedText: '',
          llmResponse: ''
        });
        audioChunksRef.current = []; // Clear audio chunks

        // Cleanup audio playback service
        audioPlaybackService.cleanup();
        
        // Ensure background listening is restarted when returning to listening mode
        if (isBackgroundListening && DeviceUtils.supportsWebAudio()) {
          try {
            // Initialize and restart global audio manager
            await globalAudioManager.initialize();
            if (!globalAudioManager.isPersistentMode) {
              await globalAudioManager.startPersistentProcessing();
            }
            
            // Dispatch event to notify VoiceVisualizer to restart audio processing
            window.dispatchEvent(new CustomEvent('queryModeEnded'));
          } catch (error) {
            console.warn('Failed to restart background listening after query mode:', error);
          }
        }
      }, 3000);
    }
  };

  // Add function to send text query to backend
  const sendTextQueryToBackend = async (queryText) => {
    try {
      // Set processing state to true
      updateDashboardState({ 
        isTextProcessing: true,
        textResponse: '' 
      });
      
      const token = localStorage.getItem('sarvik_token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const requestBody = {
        token: token,
        query: queryText
      };
      
      console.log('Sending text query to /api/query/text-stream endpoint');
      
      // Use the correct backend URL (port 8000) instead of frontend URL (port 3000)
      const backendURL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      
      // Use fetch with streaming response
      const response = await fetch(`${backendURL}/api/query/text-stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Process the streaming response
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedResponse = '';
      
      try {
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            // Streaming is complete
            break;
          }
          
          // Decode the chunk and process SSE format
          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split('\n\n');
          
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6)); // Remove 'data: ' prefix
                
                if (data.error) {
                  console.error('LLM streaming error:', data.error);
                  updateDashboardState({ textResponse: 'Error processing your text query. Please try again.' });
                  break;
                }
                
                if (data.done) {
                  // Streaming is complete
                  break;
                }
                
                if (data.token) {
                  // Add new token to accumulated response
                  accumulatedResponse += data.token;
                  updateDashboardState({ textResponse: accumulatedResponse });
                }
              } catch (parseError) {
                console.warn('Error parsing SSE data:', parseError);
              }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }
      
    } catch (error) {
      console.error('Failed to send text query to backend:', error);
      updateDashboardState({ textResponse: 'Error processing your text query. Please try again.' });
    } finally {
      // Set processing state to false
      updateDashboardState({ isTextProcessing: false });
    }
  };

  // Function to set up voice activity detection for auto-stop
  const setupVoiceActivityDetection = (stream) => {
    try {
      // Set up MediaRecorder for audio recording
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
          console.log('Audio chunk recorded, size:', event.data.size);
        }
      };
      
      // Start recording
      mediaRecorder.start(100); // Collect data every 100ms
      console.log('MediaRecorder started for query mode');
      
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      vadAudioContextRef.current = audioContext;
      
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.8;
      
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);
      
      // Set up VAD parameters
      const vadConfig = {
        silenceThreshold: 0.005, // Minimum volume to be considered speech
        silenceDuration: 2000,   // Time in ms to wait for silence before stopping
        minSpeechDuration: 500   // Minimum speech duration before silence detection
      };
      
      let silenceStart = null;
      let speechStart = null;
      const dataArray = new Float32Array(analyser.frequencyBinCount);
      
      // VAD processing function
      const processVAD = () => {
        if (!isQueryMode || queryStatus !== 'recording') {
          return;
        }
        
        // Get audio data
        analyser.getFloatTimeDomainData(dataArray);
        
        // Calculate volume level (RMS)
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
          sum += dataArray[i] * dataArray[i];
        }
        const volume = Math.sqrt(sum / dataArray.length);
        
        const now = Date.now();
        
        // Check if currently speaking
        if (volume > vadConfig.silenceThreshold) {
          // Voice detected
          if (!speechStart) {
            speechStart = now;
          }
          silenceStart = null;
        } else {
          // Silence detected
          if (speechStart && (now - speechStart) > vadConfig.minSpeechDuration) {
            if (!silenceStart) {
              silenceStart = now;
            } else if ((now - silenceStart) > vadConfig.silenceDuration) {
              // Enough silence detected, auto-stop query mode
              console.log('🔇 Auto-stopping query mode due to silence (2 seconds of no speech)');
              stopQueryMode();
              return;
            }
          }
        }
        
        // Continue processing
        requestAnimationFrame(processVAD);
      };
      
      // Start VAD processing
      processVAD();
      
    } catch (error) {
      console.error('Failed to set up voice activity detection:', error);
    }
  };

  // Voice Activity Detection timeout reference
  const vadTimeoutRef = useRef(null);
  
  const autoStopTimerRef = useRef(null);
  
  const vadAudioContextRef = useRef(null);

  // Function to start query mode
  const startQueryMode = async () => {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        console.warn('⚠️ Recording already in progress, ignoring new request');
        return;
      }
      
      updateDashboardState({ 
        isQueryMode: true,
        queryStatus: 'recording'
      });
      
      // Use the same device that global audio manager is using for consistency
      // Get the current device from deviceManager
      const currentDevice = deviceManager.getCurrentInputDevice();
      
      // Request a dedicated audio stream using the same device constraints
      const constraints = {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100,
          channelCount: 1
        }
      };
      
      // If we have a specific device, use it
      if (currentDevice && currentDevice.deviceId && currentDevice.deviceId !== 'default') {
        constraints.audio.deviceId = { exact: currentDevice.deviceId };
      }
      
      // Get a dedicated audio stream for query mode using the same device as global audio manager
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      updateDashboardState({ queryAudioStream: stream });
      
      // Set up voice activity detection for auto-stop
      setupVoiceActivityDetection(stream);
      
      const AUTO_STOP_DURATION = 180000; // 180 seconds = 3 minutes
      autoStopTimerRef.current = setTimeout(() => {
        console.log(`⏱️ Auto-stopping query mode after ${AUTO_STOP_DURATION / 1000} seconds (3 minutes)`);
        console.log('⏱️ Timer fired - calling stopQueryMode()');
        stopQueryMode();
      }, AUTO_STOP_DURATION);
      
      console.log(`✅ Auto-stop timer set for ${AUTO_STOP_DURATION / 1000} seconds (3 minutes)`);
      console.log('Query mode started - recording user input with device:', currentDevice?.label || 'default');
    } catch (error) {
      console.error('Failed to start query mode:', error);
      updateDashboardState({ 
        isQueryMode: false,
        queryStatus: 'idle'
      });
    }
  };

  const handleSettingsClick = () => {
    console.log('Navigating to account settings');
    
    // Navigate to account settings (no highlighting needed)
    if (onNavigate) {
      onNavigate('account-settings');
    }
  };

  const handleToastSettingsClick = () => {
    console.log('Navigating to app settings with highlight');
    
    // Navigate to app settings with highlighting for background settings
    if (onNavigate) {
      onNavigate('user-settings', {
        activeTab: 'app',
        highlightBackgroundSettings: true,
        highlightActiveHours: true
      });
    }
  };

  const handleCloseToast = () => {
    setShowFirstTimeToast(false);
  };

  // Add the toggleMode function
  const toggleMode = () => {
    // If switching to text mode, stop any active query mode without processing audio
    if (!isTextMode) {
      if (isQueryMode) {
        stopQueryMode(false); // Don't process audio when switching modes
      }
      // Clear voice mode states when switching to text mode
      updateDashboardState({ 
        queryStatus: 'idle',
        transcribedText: '',
        llmResponse: ''
      });
      // Clear audio chunks to prevent re-sending
      audioChunksRef.current = [];
      // setShowTextQueryInput is removed since we're using the new text mode UI
    } else {
      // Switching from text mode to voice mode, clear text mode states
      updateDashboardState({ textResponse: '' });
    }
    
    updateDashboardState({ isTextMode: !isTextMode });
  };

  const handleQueryAction = () => {
    if (isQueryMode) {
      stopQueryMode();
    } else {
      startQueryMode();
    }
  };

  return (
    <DashboardContainer>
      <Header>
        <WelcomeSection>
          <WelcomeText>
            {getGreeting()}, {user?.displayName?.split(' ')[0] || 'User'}
          </WelcomeText>
          <SubText>
            Your AI assistant is ready to help
          </SubText>
        </WelcomeSection>

        <QuickActions>
          {/* Add the circular mode toggle button with tooltip */}
          <div style={{ position: 'relative' }}>
            <ModeToggleButton
              $isTextMode={isTextMode}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={toggleMode}
              onMouseEnter={() => setIsTooltipVisible(true)}
              onMouseLeave={() => setIsTooltipVisible(false)}
            >
              {isTextMode ? <Mic size={20} /> : <MessageSquare size={20} />}
            </ModeToggleButton>
            <ModeToggleTooltip className={isTooltipVisible ? 'visible' : ''}>
              {isTextMode ? 'Switch to Voice Mode' : 'Switch to Text Mode'}
            </ModeToggleTooltip>
          </div>
          
          <ActionButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleSettingsClick}
          >
            <Settings />
            Settings
          </ActionButton>
        </QuickActions>
      </Header>

      <MainContent>
        <CenterPanel>
          <BackgroundGradient />
          
          {/* Conditional rendering based on mode */}
          <AnimatePresence mode="wait">
            {isTextMode ? (
              // Text Mode UI
              <TextModeContainer
                key="text-mode"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <TextModeHeader>
                  <TextModeTitle>Text Mode</TextModeTitle>
                </TextModeHeader>
                
                <TextModeContent>
                  <TextResponseArea ref={textResponseRef}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{textResponse || ""}</ReactMarkdown>
                  </TextResponseArea>
                  
                  <TextInputContainer>
                    <TextQueryArea
                      value={textQuery}
                      onChange={(e) => updateDashboardState({ textQuery: e.target.value })}
                      placeholder="Type your query here..."
                      onKeyDown={(e) => {
                        // Handle CTRL+Enter shortcut
                        if (e.ctrlKey && e.key === 'Enter' && textQuery.trim() && !isTextProcessing) {
                          sendTextQueryToBackend(textQuery);
                          // Clear the text query after sending
                          updateDashboardState({ textQuery: '' });
                        }
                      }}
                    />
                    <TextSendButtonContainer>
                      <TextSubmitButton
                        isProcessing={isTextProcessing}
                        whileHover={{ scale: isTextProcessing ? 1 : 1.05 }}
                        whileTap={{ scale: isTextProcessing ? 1 : 0.95 }}
                        onClick={() => {
                          if (textQuery.trim() && !isTextProcessing) {
                            sendTextQueryToBackend(textQuery);
                            // Clear the text query after sending
                            updateDashboardState({ textQuery: '' });
                          }
                        }}
                        disabled={!textQuery.trim() || isTextProcessing || textQuery.length > 1000}
                      >
                        {isTextProcessing ? (
                          <LoadingDots style={{ width: '40px', height: '20px' }}>
                            <span></span>
                            <span></span>
                            <span></span>
                          </LoadingDots>
                        ) : (
                          <>
                            <SendButtonText>Send</SendButtonText>
                            <ShortcutText>CTRL+Enter</ShortcutText>
                          </>
                        )}
                      </TextSubmitButton>
                    </TextSendButtonContainer>
                    <TextQueryCounter>
                      {textQuery.length}/1000
                    </TextQueryCounter>
                  </TextInputContainer>
                </TextModeContent>
              </TextModeContainer>
            ) : (
              // Voice Mode UI (existing content)
              <motion.div
                key="voice-mode"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                style={{ width: '100%', height: '100%' }}
              >
                {/* Always use VoiceVisualizer to maintain status label and buttons */}
                <VoiceVisualizer 
                  key="universal-voice-visualizer"
                  isBackgroundListening={isBackgroundListening && (isListening || connectionStatus === 'connected')}
                  onEnableListening={handleEnableListening}
                  onSettingsClick={handleToastSettingsClick}
                  voiceData={voiceData}
                  platformConfig={platformConfig}
                  isMobile={isMobileDevice}
                  showButton={showButton}
                  // Query mode props
                  onQueryStart={startQueryMode}
                  onQueryStop={stopQueryMode}
                  isQueryMode={isQueryMode}
                  queryStatus={queryStatus}
                  // Pass query audio stream for visualization
                  queryAudioStream={queryAudioStream}
                  // Pass transcribed text to visualizer
                  transcribedText={transcribedText}
                  // Pass LLM response to visualizer
                  llmResponse={llmResponse}
                />
                
                {/* Ask Anything Button */}
                <AnimatePresence>
                  {showButton && !isQueryMode && (
                    <QueryButton
                      key="ask-anything-button"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{ 
                        duration: isMobileDevice ? 0.4 : 0.3,
                        ease: "easeOut"
                      }}
                      onClick={handleQueryAction}
                      style={{
                        fontSize: isMobileDevice ? '0.9rem' : '1rem',
                        padding: isMobileDevice ? '12px 24px' : '16px 32px',
                        minHeight: isMobileDevice ? '48px' : 'auto',
                        touchAction: 'manipulation',
                        userSelect: 'none'
                      }}
                    >
                      <Mic size={16} />
                      Ask Anything
                    </QueryButton>
                  )}
                </AnimatePresence>
                
                <FirstTimeToast 
                  isVisible={showFirstTimeToast}
                  onClose={handleCloseToast}
                  onOpenSettings={handleToastSettingsClick}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </CenterPanel>

        <CollapsibleStatus status={connectionStatus} />
      </MainContent>
    </DashboardContainer>
  );
};

export default Dashboard;
